import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  ISSUER_ALL_ASSETS_LIST_QUERY_KEY,
  ISSUER_PORTFOLIOS_LIST_QUERY_KEY,
  ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY,
  ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  CreateRegisterBankRequest,
  GetRegisterBankListResponse,
  GetRegisterBankListV1SrRegisterBanksGetParams,
  UpdateRegisterBankRequest,
} from 'api';

export const REGISTER_BANKS_LIST_QUERY_KEY = 'vestra/issuer-register-banks';

export const useRegisterBanksListQuery = (
  payload: GetRegisterBankListV1SrRegisterBanksGetParams & { registerId?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { registerId, ...data } = payload;
  return useQuery<GetRegisterBankListResponse>({
    queryKey: [REGISTER_BANKS_LIST_QUERY_KEY, registerId || register_id, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getRegisterBankListV1SrRegisterBanksGet(data, {
        headers: { 'xRegister-ID': registerId || register_id },
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useRegisterBanksListLazyQuery = (
  payload: GetRegisterBankListV1SrRegisterBanksGetParams & { registerId?: number },
  disabled = false,
) => {
  const [params] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const { page, registerId, ...data } = payload;
  return useInfiniteQuery<GetRegisterBankListResponse>({
    queryKey: [REGISTER_BANKS_LIST_QUERY_KEY, registerId || register_id, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam }) => {
      const response = await api.v1.getRegisterBankListV1SrRegisterBanksGet(
        {
          ...payload,
          page: pageParam || page,
        },
        {
          headers: { 'xRegister-ID': register_id || registerId },
        },
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useIssuerDeleteBankMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (registerBankId: number) => {
      const response = await withErrorHandling(
        api.v1.deleteRegisterBankV1SrRegisterBanksRegisterBankIdDelete({ registerBankId }),
        {
          loading: 'Deleting bank...',
          success: 'Bank deleted',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIOS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({ queryKey: [ISSUER_ALL_ASSETS_LIST_QUERY_KEY] });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerAddBankMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateRegisterBankRequest) => {
      const response = await withErrorHandling(
        api.v1.createRegisterBankV1SrRegisterBanksPost(payload),
        {
          loading: 'Adding bank...',
          success: 'Bank added',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIOS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({ queryKey: [ISSUER_ALL_ASSETS_LIST_QUERY_KEY] });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });

      onSuccess && onSuccess();
    },
  });
};

export const useIssuerUpdateBankMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
}) => {
  return useMutation({
    mutationFn: async (payload: UpdateRegisterBankRequest) => {
      const response = await withErrorHandling(
        api.v1.updateRegisterBankV1SrRegisterBanksPatch(payload),
        {
          loading: 'Updating bank...',
          success: 'Bank updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_BANKS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIOS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({ queryKey: [ISSUER_ALL_ASSETS_LIST_QUERY_KEY] });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TREEMAP_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_BREAKDOWN_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_PNL_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_TOTAL_CASH_IN_OUT_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_VALUE_OVERTIME_CHART_BY_ID_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_PORTFOLIO_OVERALL_BALANCE_QUERY_KEY],
      });

      onSuccess && onSuccess();
    },
  });
};
